import {
  FALLBACK_REGION_ID,
  REGION_DETAILS_LOCAL_STORAGE_KEY,
  TAbsoluteLocale,
  TAbsoluteLocaleStandard,
  TCountryStandard,
  TLocalisationDataLocalStorage,
} from '@src/config/i18n.config'
import { REGION_ID_HEADER, REGION_LANG_HEADER } from '@src/constants/api.headers.constants'
import { CONTENT_VERSION_HEADER } from '@src/constants/content-version.constants'
import { CONTENT_PREVIEW_BLACKLIST_DOMAIN } from '@src/constants/contentPreviewBlacklistDomains'
import { LOCALISATION_WHITELIST_DOMAIN } from '@src/constants/localizationWhitelistDomain'
import { getFromLocalStorageWithExpiry } from '@src/quince-site-common/src/utils/getFromLocalStorageWithExpiry'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { setInLocalStorageWithExpiry } from '@src/quince-site-common/src/utils/setInLocalStorageWithExpiry'
import type { TRegionDetail } from '@src/server-side/api/fetch-region-details'
import { shouldEnableI18nClient } from '@src/server-side/utils/i18n-preview.utils'

import { isNonNullish } from './functions/utils'
import { getStandardLocale, parseLocale } from './i18n.utils'

import type { AxiosRequestHeaders } from 'axios'

export interface IRegionAndLocaleHeaderObject {
  [REGION_ID_HEADER]: string
  [REGION_LANG_HEADER]: string
}

const BATCH_SIZE = 15

export function _intersection<T>(left: T[], right: T[], comparator?: (leftItem: T, rightItem: T) => boolean): T[] {
  if (!Array.isArray(left) || !Array.isArray(right)) return []
  if (comparator) {
    return left.filter(leftItem => right.some(rightItem => comparator(leftItem, rightItem)))
  }
  return left.filter(leftItem => right.includes(leftItem))
}

export function disectQueryString(query: string): Record<string, string[]> {
  const searchParams = new URLSearchParams(query)
  const result: Record<string, string[]> = {}
  for (const key of searchParams.keys()) {
    if (searchParams.get(key)) {
      result[key] = searchParams.get(key)?.split(',') ?? []
    }
  }
  return result
}

export function tranformToMap(record: Record<string, string | string[] | undefined>) {
  const result: Record<string, string> = {}
  Object.entries(record).forEach(([key, values]) => {
    if (!values) return
    result[key] = Array.isArray(values) ? values.join(',') : values
  })

  return result
}

export function extractPreviewHash(hostname: string): string | null {
  const regex = /^release-([a-zA-Z0-9]+)\.preview(-qa|-dev)?\.quince\.com$/
  const match = hostname.match(regex)
  return match?.[1] ?? null
}

export function isPreviewEnv(hostname: string): boolean {
  return (
    hostname === 'preview.quince.com' ||
    hostname.endsWith('.preview.quince.com') ||
    hostname === 'storefront-ssr-auto-preview-internal.onequince.com'
  )
}

export const getHeadersWithContentVersion = (
  headers: HeadersInit | AxiosRequestHeaders | undefined,
  domain?: string,
): HeadersInit | AxiosRequestHeaders | undefined => {
  if (!isBrowser()) return headers
  if (domain && _intersection(CONTENT_PREVIEW_BLACKLIST_DOMAIN, [domain]).length > 0) return headers
  const contentVersion = getContentVersionOnClient()
  if (contentVersion) {
    const newHeaders = { ...headers } as Record<string, string>
    newHeaders[CONTENT_VERSION_HEADER] = contentVersion
    return newHeaders
  }
  return headers
}

export const getContentVersionOnClient = (): string | null => {
  // Preview only works on Non-prod hosts ( doesn't work on www.quince.com )
  if (!isBrowser()) return null
  if (isProdHost(window.location.host)) return null
  let contentVersion: string | null = null

  // Extract from storage
  contentVersion = extractPreviewHash(window.location.host)

  if (contentVersion === 'latest') return null // if you are on latest version, return null
  return contentVersion ?? null
}

export const isProdHost = (host: string | undefined): boolean => {
  return typeof host === 'string' && (host === 'www.quince.com' || host === 'quince.com')
}

export const transformTagsIntoAnalyticsTags = (tags: (string | null)[] | null): string[] => {
  return tags?.filter(isNonNullish).map(tag => tag.replace(/ /g, '_').toLowerCase()) ?? []
}

const isLocalisationWhitelistedDomain = (domain?: string): boolean => {
  try {
    if (!domain) return false
    const inputUrl = new URL(domain)
    return LOCALISATION_WHITELIST_DOMAIN.some(whitelistedDomain => {
      if (!whitelistedDomain) return false
      try {
        // Handle both domain-only and full URL whitelist entries
        const whitelistedUrl = new URL(whitelistedDomain)
        return inputUrl.origin === whitelistedUrl.origin
      } catch {
        // If whitelistedDomain is not a valid URL, compare with origin
        return inputUrl.origin.includes(whitelistedDomain)
      }
    })
  } catch (error) {
    console.error('Error checking localisation whitelist domain:', error)
    return false
  }
}

/**
 * ⚠️⚠️⚠️ CLIENT SIDE ONLY - DO NOT USE ON SERVER SIDE ⚠️⚠️⚠️
 *
 * WARNING: This function can cause hydration mismatches in Next.js 15+
 * Use getLocalisationDataWithFallbacks() for telemetry/analytics instead
 * Use getLocalisationDataFromStorageHydrationSafe() for components that need localStorage data
 */
export const getLocalisationDataFromStorage = (): TLocalisationDataLocalStorage | null => {
  // TODO: The server should read it properly, and the client should only read from the Context, and not from the storage
  if (!isBrowser() || !shouldEnableI18nClient()) {
    return null
  }

  let rawData: Partial<TLocalisationDataLocalStorage> | null = null
  try {
    rawData = JSON.parse(
      // eslint-disable-next-line i18next/no-literal-string
      getFromLocalStorageWithExpiry(REGION_DETAILS_LOCAL_STORAGE_KEY) ?? 'null',
    ) as Partial<TLocalisationDataLocalStorage> | null
  } catch (error) {
    console.error('Error parsing localisation data from storage:', error)
    return null
  }

  // Add fallback values for currency, country, and language if they are undefined
  if (rawData) {
    return {
      ...rawData,
      currency: rawData.currency ?? 'USD',
      country: rawData.country ?? 'US',
      language: rawData.language ?? 'en',
    } as TLocalisationDataLocalStorage
  }

  return null
}

/**
 * Gets localization data with fallback values for analytics/telemetry
 * Returns default values when localization data is not available (e.g., during SSR)
 * This function is hydration-safe and should be used instead of getLocalisationDataFromStorage
 * for telemetry and analytics purposes.
 */
export const getLocalisationDataWithFallbacks = (): {
  currency: string
  country: string
  language: string
  locale: string
} => {
  const localisationData = getLocalisationDataFromStorage()
  const EN = 'en'
  const US = 'US'
  const USD = 'USD'
  const EN_US = 'en-US'
  return {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    currency: localisationData?.currency || USD,
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    country: localisationData?.country || US,
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    language: localisationData?.language || EN,
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    locale: localisationData?.locale || EN_US,
  }
}

/**
 * Hydration-safe version of getLocalisationDataFromStorage
 * Only returns data after the component has mounted on the client
 * Use this when you specifically need the localStorage data and can handle the async nature
 */
export const getLocalisationDataFromStorageHydrationSafe = (): Promise<TLocalisationDataLocalStorage | null> => {
  return new Promise(resolve => {
    if (!isBrowser()) {
      resolve(null)
      return
    }

    // Use requestAnimationFrame to ensure we're after hydration
    requestAnimationFrame(() => {
      resolve(getLocalisationDataFromStorage())
    })
  })
}

export const getRegionIdFromLocale = (locale: TAbsoluteLocale, regionDetails: TRegionDetail): number => {
  const { country } = parseLocale(locale)
  const { regionId } = regionDetails.regions[country] ?? { regionId: null }
  return regionId ?? FALLBACK_REGION_ID
}

export const setLocalisationDataInStorage = (locale: TAbsoluteLocale, regionDetails: TRegionDetail) => {
  const { country, language } = parseLocale(locale)
  const { regionId } = regionDetails.regions[country] ?? { regionId: null }
  const currency = regionDetails.regions[country]?.defaultCurrencyIsoCode
  setInLocalStorageWithExpiry(
    REGION_DETAILS_LOCAL_STORAGE_KEY,
    JSON.stringify({
      regionId,
      locale: getStandardLocale(locale),
      country: country.toUpperCase() as TCountryStandard,
      language,
      currency,
    }),
  )
}

// getRegionAndLocaleHeaderObject always returns the standard locale
// As this function is being used in the server side and for downstreams, we need to ensure that the locale is always the standard locale
export const getRegionAndLocaleHeaderObject = (
  locale: TAbsoluteLocale | TAbsoluteLocaleStandard,
  regionId: number,
  isI18nPreviewEnabled: boolean,
): IRegionAndLocaleHeaderObject | Record<string, never> => {
  if (!isI18nPreviewEnabled) return {}
  return {
    [REGION_ID_HEADER]: String(regionId),
    [REGION_LANG_HEADER]: String(getStandardLocale(locale)),
  }
}

/**
 * ⚠️⚠️⚠️ CLIENT SIDE ONLY - DO NOT USE ON SERVER SIDE ⚠️⚠️⚠️
 */
export const getLocalisationHeaders = (domain?: string): Record<string, string> => {
  const localisationData = getLocalisationDataFromStorage()
  try {
    const headers = localisationData
      ? {
          [REGION_ID_HEADER]: localisationData.regionId,
          [REGION_LANG_HEADER]: getStandardLocale(localisationData.locale),
        }
      : {}
    // If domain is provided and not whitelisted, return empty object
    if (domain && !isLocalisationWhitelistedDomain(domain)) {
      return {}
    }

    // Cast all values to string and filter out undefined/null
    return Object.entries(headers).reduce<Record<string, string>>(
      (acc, [key, value]) =>
        value
          ? {
              ...acc,
              [key]: String(value),
            }
          : acc,
      {},
    )
  } catch (error) {
    console.error('Error getting localisation headers:', error)
    return {}
  }
}

export function parseFormat(format: string, input: string): Record<string, string> | null {
  const varNames: string[] = []
  let matchCount = 0
  const regexPattern = format.replace(/{{(.*?)}}/g, (_, varName: string) => {
    varNames.push(varName)
    matchCount++
    return '(.+?)' // non-greedy capture group
  })

  const regex = new RegExp(`^${regexPattern}$`)
  const match = input.match(regex)

  if (!match || match.length < matchCount + 1) return null

  const result: Record<string, string> = {}
  varNames.forEach((name, index) => {
    result[name] = match[index + 1] ?? ''
  })

  return result
}

export const parseJwt = (token: string) => {
  if (!token) return null
  // this function is used to parse the JWT token from which we are getting the storeProgramIds to check if the user is authorized to access the page
  const base64Url = token.split('.')[1]
  const base64 = base64Url?.replace(/-/g, '+').replace(/_/g, '/') ?? ''
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join(''),
  )
  return JSON.parse(jsonPayload) as Record<string, unknown>
}

export function createBatches<T>(array: T[], batchSize: number = BATCH_SIZE): T[][] {
  const batches: T[][] = []
  for (let i = 0; i < array.length; i += batchSize) {
    batches.push(array.slice(i, i + batchSize))
  }
  return batches
}

export function filterFulfilled<T>(results: PromiseSettledResult<T>[]): T[] {
  return results.filter(result => result.status === 'fulfilled').map(result => result.value)
}
